import { Link } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import { useAuth } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Shield, ClipboardCheck, Monitor, Cpu, Factory, UserCheck,
  GraduationCap, Clock, CalendarDays, Download, ExternalLink, FileText,
  CheckCircle2, ArrowRight, BookOpen,
} from "lucide-react";

const CATALOG_PDF_URL =
  "https://customer-assets.emergentagent.com/job_d39601e6-291a-4b28-8f35-4ec39f3b7968/artifacts/9ovraav6_Aspiritech_Academy_Academic_Catalog_2026_Edition_Updated.docx.pdf";

const PROGRAMS = [
  {
    name: "Registered Cybersecurity Pre-Apprenticeship RTI Program",
    icon: Shield,
    credential: "Prepares for CompTIA Security+ (SY0-701)",
    duration: "64 weeks (12 wks RTI + 52 wks paid OJT)",
    format: "100% Virtual · Mon–Thu, 9 a.m.–12 p.m. CT",
    description:
      "Classroom technical instruction for a U.S. Department of Labor Registered Apprenticeship — the foundation for a cybersecurity career, with a paid on-the-job training track for Illinois residents.",
    topics: ["Threat landscape", "Incident response", "CMMC framework", "Cryptography", "Identity & access management", "Network security"],
    eligibility: "18+, U.S. resident. IL residents eligible for paid OJT apprenticeship.",
  },
  {
    name: "AI Quality Assurance (AIQA) Program",
    icon: ClipboardCheck,
    credential: "Prepares for ISTQB Certified Tester Foundation Level (CTFL)",
    duration: "6 weeks",
    format: "100% Virtual · Mon–Fri, 9 a.m.–5 p.m. CT",
    description:
      "Hands-on training in software quality assurance principles and industry-standard testing methodologies used by real QA teams.",
    topics: ["SDLC", "Test design", "TestRail", "Defect logging", "Automated testing awareness"],
    eligibility: "18+, U.S. resident.",
  },
  {
    name: "IT Foundations Program",
    icon: Monitor,
    credential: "Prepares for CompTIA Tech+, A+, and Google Digital Career Certificate",
    duration: "12 weeks (Beginners & Intermediate tracks)",
    format: "100% Virtual · Mon–Fri, 9 a.m.–12 p.m. CT",
    description:
      "Build foundational technology competencies — from hardware and operating systems to networking and troubleshooting — across Beginners (Tech+) and Intermediate (A+) tracks.",
    topics: ["Computer hardware", "Operating systems", "Networking", "Cybersecurity basics", "Cloud technology", "Troubleshooting"],
    eligibility: "18+, U.S. resident, high school diploma / GED.",
  },
  {
    name: "Professional AI Program",
    icon: Cpu,
    credential: "Prepares for ADaSci Certified Vibe Coding Practitioner or HubSpot Digital Marketing",
    duration: "6–16 weeks (varies by track)",
    format: "100% Virtual · Schedule varies by track",
    description:
      "Learn to put AI to work in professional and technical settings — across Vibe Coding, AI Professional, AI for Data Sciences, and Digital Marketing tracks.",
    topics: ["Generative AI tools", "Prompt engineering", "Data analysis", "Ethical AI", "Workflow automation"],
    eligibility: "18+, U.S. resident, high school diploma / GED.",
  },
  {
    name: "Manufacturing Program",
    icon: Factory,
    credential: "Prepares for SME Certified Manufacturing Associate (CMfgA)",
    duration: "12 weeks",
    format: "100% Virtual · Mon–Fri, 9 a.m.–12 p.m. CT",
    description:
      "Prepare for careers in modern, automated, and AI-enabled manufacturing environments.",
    topics: ["Manufacturing processes", "Safety", "Quality systems", "CNC", "Robotics", "AI in manufacturing"],
    eligibility: "18+, U.S. resident, high school diploma / GED.",
  },
  {
    name: "Human Resources Program",
    icon: UserCheck,
    credential: "Prepares for HRCI Associate Professional in Human Resources (aPHR)",
    duration: "12 weeks",
    format: "100% Virtual · Mon–Fri, 9 a.m.–12 p.m. CT",
    description:
      "Launch a career in HR, talent acquisition, and organizational operations — with a focus on building neuroinclusive workplaces.",
    topics: ["Talent acquisition", "Employment law", "Total rewards", "Neuroinclusive practices", "HR technology"],
    eligibility: "18+, U.S. resident, high school diploma / GED.",
  },
];

const ProgramCard = ({ program }) => {
  const Icon = program.icon;
  return (
    <Card className="bg-white rounded-2xl border border-slate-200/60 shadow-sm hover:shadow-md transition-shadow" data-testid={`catalog-program-${program.name.slice(0, 12)}`}>
      <CardContent className="p-6 sm:p-7">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 bg-brand-navy/10 rounded-xl flex items-center justify-center flex-shrink-0">
            <Icon className="w-6 h-6 text-brand-navy" />
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-heading font-bold text-slate-900 leading-snug">{program.name}</h3>
            <span className="inline-flex items-center gap-1 text-xs font-medium bg-brand-gold/15 text-brand-navy px-2.5 py-1 rounded-full mt-2">
              <GraduationCap className="w-3 h-3" /> {program.credential}
            </span>
          </div>
        </div>

        <p className="text-sm text-slate-600 leading-relaxed mt-4">{program.description}</p>

        <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4 text-sm text-slate-500">
          <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-brand-navy" /> {program.duration}</span>
          <span className="flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-brand-navy" /> {program.format}</span>
        </div>

        <div className="mt-4">
          <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold mb-2">What you'll learn</p>
          <div className="flex flex-wrap gap-1.5">
            {program.topics.map((t) => (
              <span key={t} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-md">{t}</span>
            ))}
          </div>
        </div>

        <p className="text-xs text-slate-500 mt-4 flex items-start gap-1.5">
          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 mt-0.5 flex-shrink-0" />
          <span><span className="font-semibold text-slate-600">Eligibility:</span> {program.eligibility} · No tuition — $99 application/reservation fee.</span>
        </p>
      </CardContent>
    </Card>
  );
};

const AcademicCatalog = () => {
  const { user, login } = useAuth();
  return (
    <div className="min-h-screen bg-surface-app" data-testid="academic-catalog-page">
      <Header />

      {/* Hero */}
      <section className="bg-brand-navy pt-28 pb-16 md:pt-32 md:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E3A8A] via-[#152C6E] to-[#0F1D4A]" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-brand-gold text-brand-navy border-brand-gold mb-6 font-semibold">2026 Edition</Badge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold text-white mb-5 tracking-tight">Academic Catalog</h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto leading-relaxed">
            Career-focused programs built for neurodivergent adults. Browse the summary below or read the full catalog.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
            <a href={CATALOG_PDF_URL} download data-testid="catalog-download-btn">
              <Button className="btn-primary px-6 py-3 rounded-xl gap-2"><Download className="w-4 h-4" /> Download Catalog (PDF)</Button>
            </a>
            <a href={CATALOG_PDF_URL} target="_blank" rel="noopener noreferrer" data-testid="catalog-open-btn">
              <Button variant="outline" className="px-6 py-3 rounded-xl gap-2 bg-white/10 text-white border-white/30 hover:bg-white/20"><ExternalLink className="w-4 h-4" /> Open in new tab</Button>
            </a>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-12">
        {/* Mission */}
        <Card className="bg-white rounded-2xl border border-slate-200/60 shadow-sm">
          <CardContent className="p-6 sm:p-8">
            <p className="text-[11px] uppercase tracking-wider text-brand-gold font-bold mb-2">Our Mission</p>
            <p className="text-lg text-slate-700 leading-relaxed font-heading">
              "Every cohort, every curriculum module, every partnership comes from one belief: that neurodivergent minds are not a
              problem to be accommodated. They are an asset to be invested in."
            </p>
          </CardContent>
        </Card>

        {/* Program summary */}
        <div>
          <div className="flex items-center gap-2 mb-1">
            <BookOpen className="w-5 h-5 text-brand-navy" />
            <h2 className="text-2xl font-heading font-bold text-slate-900">Programs at a glance</h2>
          </div>
          <p className="text-slate-500 mb-6 text-sm">Six career pathways — all 100% virtual, all tuition-free.</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {PROGRAMS.map((p) => <ProgramCard key={p.name} program={p} />)}
          </div>
        </div>

        {/* Admissions + accreditation */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Card className="bg-white rounded-2xl border border-slate-200/60 shadow-sm">
            <CardContent className="p-6">
              <h3 className="font-heading font-bold text-slate-900 mb-3">How admissions work</h3>
              <ol className="space-y-2 text-sm text-slate-600">
                <li className="flex gap-2"><span className="font-bold text-brand-navy">1.</span> Complete the intake form</li>
                <li className="flex gap-2"><span className="font-bold text-brand-navy">2.</span> Application review</li>
                <li className="flex gap-2"><span className="font-bold text-brand-navy">3.</span> 1:1 meeting with an instructor</li>
                <li className="flex gap-2"><span className="font-bold text-brand-navy">4.</span> Acceptance & onboarding (plus a Working-Style Quiz to tailor your experience)</li>
              </ol>
              <p className="text-xs text-slate-500 mt-3">A formal diagnosis is generally not required to enroll. You'll need to be 18+, a U.S. resident, and have a computer with reliable internet.</p>
            </CardContent>
          </Card>
          <Card className="bg-white rounded-2xl border border-slate-200/60 shadow-sm">
            <CardContent className="p-6">
              <h3 className="font-heading font-bold text-slate-900 mb-3">Accreditation & approval</h3>
              <ul className="space-y-2 text-sm text-slate-600">
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" /> Cybersecurity Pre-Apprenticeship RTI is a U.S. DOL Registered Apprenticeship, approved under WIOA for Illinois & Ohio.</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" /> Seeking ACCET accreditation (target Dec 2027).</li>
                <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-slate-300 mt-0.5 flex-shrink-0" /> Other programs are awaiting / seeking formal IBHE/state approval.</li>
              </ul>
            </CardContent>
          </Card>
        </div>

        {/* Embedded PDF */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <FileText className="w-5 h-5 text-brand-navy" />
            <h2 className="text-2xl font-heading font-bold text-slate-900">Read the full catalog</h2>
          </div>
          <div className="rounded-2xl border border-slate-200 overflow-hidden bg-white shadow-sm">
            <iframe
              src={CATALOG_PDF_URL}
              title="Aspiritech Academy Academic Catalog 2026"
              className="w-full"
              style={{ height: "80vh", minHeight: 600 }}
              data-testid="catalog-pdf-iframe"
            />
          </div>
          <p className="text-xs text-slate-400 mt-2">Trouble viewing? <a href={CATALOG_PDF_URL} target="_blank" rel="noopener noreferrer" className="text-brand-navy underline">Open the PDF in a new tab</a> or download it above.</p>
        </div>

        {/* CTA */}
        <div className="text-center bg-slate-50 rounded-2xl py-12 px-4">
          <h2 className="text-2xl font-heading font-semibold text-slate-900 mb-3">Ready to start?</h2>
          <p className="text-slate-600 mb-6">Enrollment is free. Pick your program and preferred start date.</p>
          {user ? (
            <Link to="/apply"><Button className="btn-primary px-8 py-3 rounded-xl text-base" data-testid="catalog-cta-enroll">Begin Enrollment <ArrowRight className="w-5 h-5 ml-2" /></Button></Link>
          ) : (
            <Button className="btn-primary px-8 py-3 rounded-xl text-base" onClick={login} data-testid="catalog-cta-enroll">Sign In to Enroll <ArrowRight className="w-5 h-5 ml-2" /></Button>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AcademicCatalog;
